'use client'

import { IconInfo } from '@/app/components/atoms/Icons'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { NO_APLICA } from '@/app/lib/analytics/constants'
import { cx } from '@/app/lib/utils'

import styles from './NoticeBanner.module.scss'
import type { NoticeBannerProps } from './types'

const ICON_SIZE = 20 // Figma: 20×20, el propio viewBox del icono

export function NoticeBanner({
  variant,
  message,
  linkLabel,
  scrollTargetId,
  marquee = false,
  className,
  analytics,
}: NoticeBannerProps) {
  const pageAnalytics = useOptionalPageAnalytics()
  // Un único botón con TODO el contenido dentro: la banda entera es
  // clickable, no solo el «consúltalas aquí». Su nombre accesible es la frase
  // completa (mensaje + acción), que es exactamente lo que se lee en pantalla.
  // El icono queda decorativo: el mensaje ya dice lo mismo, así que nombrarlo
  // solo duplicaría la frase (WCAG 1.1.1).
  const content = (
    <>
      {/* El icono va DENTRO del mensaje, no como hermano suyo: en el layout
          estático la banda se apila en columna (móvil y tablet) y de otro modo
          caería en una línea propia. */}
      <span className={styles.message}>
        {variant === 'warning' && <IconInfo size={ICON_SIZE} className={styles.icon} />}
        {message}
      </span>
      {/* Espacio explícito, no solo el `gap` del flex: separa las dos mitades
          de la frase también al leerla (el `gap` es visual y no llega al
          nombre accesible del botón). Flexbox no lo pinta como ítem propio,
          así que no duplica la separación. */}{' '}
      {/* El subrayado se queda aquí, en la parte "acción" de la frase: es la
          pista visual de que la banda lleva a algún sitio. */}
      <span className={styles.link}>{linkLabel}</span>
    </>
  )

  // Navega con `scrollIntoView`, no con un ancla `href="#id"` (mismo criterio
  // que `PerformanceSectionNav`/`AlphaListingScroller` para el resto de saltos
  // dentro de la ficha): un ancla nativa cambia el fragmento de la URL y dejar
  // que el navegador la resuelva no da margen para frenar el salto en seco.
  // El ajuste por la altura del header sticky vive en el propio destino
  // (`scroll-margin-top`), no aquí: `scrollIntoView` la respeta igual que la
  // navegación por ancla, así que el hueco se calcula en un único sitio y
  // sigue funcionando si el header cambia de alto (promo abierta/cerrada).
  //
  // Es `ui_interaction`, no `click`: no hay cambio de ruta, solo scroll dentro
  // de la misma ficha — mismo criterio que `PerformanceSectionNav`. `no_aplica`
  // en `selected_value`/`module_position`: la banda no ofrece una elección
  // entre varios destinos (un único `scrollTargetId` por banner) ni pertenece
  // a una sección propia de la ficha, es una banda de chrome que se pinta a
  // sangre antes del breadcrumb.
  function handleClick() {
    if (analytics) {
      pageAnalytics?.trackUiInteraction({
        component_type: 'notice_banner',
        component_action: 'select',
        component_id: analytics.componentId,
        component_label: analytics.componentLabel,
        selected_value: NO_APLICA,
        module_name: analytics.moduleName,
        module_type: analytics.moduleType,
        module_position: NO_APLICA,
      })
    }

    const target = document.getElementById(scrollTargetId)
    if (!target) return

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    target.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    })
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cx(
        styles.banner,
        styles[variant],
        marquee ? styles.marquee : styles.static,
        className,
      )}
    >
      {/* La marquesina necesita una pista propia: el texto va en una sola línea
          (`nowrap`) que se desplaza dentro de la banda, que hace de ventana. */}
      {marquee ? <span className={styles.track}>{content}</span> : content}
    </button>
  )
}
